import { $dy__fetchWithToken, extractDataOrFailure, useDYFetch, useDYFetchData, useDYFetchWithToken } from "~/modules/nuxt3-module-doyac/composables/fetch"
import { useDYAppConfig } from "~/modules/nuxt3-module-doyac/composables/config"
import type CityWeather from "./city-weather"
import type { ApiListStructure, ImageSet } from "~/modules/nuxt3-module-doyac/composables/common"
import type { CityFrame } from "./city-frame"
import type { DYFileFormData } from "~/modules/nuxt3-module-doyac/composables/dy-file-uploader"
import type { DataTableEmitRank } from "~/modules/nuxt3-module-doyac/components/common/DYDataTable.vue"
import type { EdgeCacheParams } from "~/modules/nuxt3-module-doyac/composables/edge-cache"

// ============================================ MODEL ============================================
export interface NationInfo {
  no: number
  countryCode: number
  isoCode2: string
  nationName: string
  nationNameEn: string
  continent: string
  region: string
  fileTypedPk?: string
  languageCode: string
  latitude: number
  longitude: number
  capital: string
  timezone: string
  images: ImageSet[]
  introduceMovieUrl?: string
  /**
   * 확대 레벨, 1이 세계지도급으로 축소, 커질수록 확대
   */
  zoomLevel: number
  /**
   * includes = ['city'] 로 요청했다면
   */
  cities?: CityFrame[]
  /**
   * 최근 날씨. 수도의 날씨를 기반으로 한다. 리스트 API에서는 기본적으로 빈 값이고 상세보기 API에서 사용된다.
   */
  latestWeather?: CityWeather
  nearbyNationsData?: NationInfo[]
}
// ============================================ MODEL ============================================

// ============================================ FILTER ===========================================

type NationFields = keyof NationInfo | '$all'
type Fields = NationFields

export interface NationInfoListParams extends EdgeCacheParams {
  /**
   * 특강 게시판 등에서 쓰는 나라명 문자열. 다수 나라도 가능 ex)프랑스 ex2)영국,일본
   */
  boardNationName?: string
  /**
   * all로 요청하면 전체 데이터 가져옴
   */
  open?: string
  item?: number
  countryCode?: number|string
  keyword?: string
  /**
   * 검색 페이지 모드(키워드 검색 + noResult 플래그). 응답 필드가 아닌 쿼리 필터 파라미터.
   */
  searchPage?: 'y'
  /**
   * 추가 데이터를 더 필요로 할 때 city 등
   */
  includes?: string[]
  $fields?: Fields[] //여기 배열이어야 함. 스트링 여러개 할당할 수 있도록
  $excludes?: Fields[] //제외 요소. 이게 fields에서 선택한것 보다 우선됨
}

export interface NationInfoGetParams extends EdgeCacheParams {
  hasNearbyNations?: boolean
  /**
   * 2026-07-07 필터필즈(over-fetch 제거) — 상세가 렌더에 실제 쓰는 필드만 요청.
   *   get/getByIsoCode2 가 JSON.stringify 해서 백엔드로 forward(show 의 filterFields 처리, 미전송 no-op).
   */
  $fields?: Fields[]
}

export interface NationInfoStoreParams {
  nationName?: string
  nationNameEn?: string
  countryCode?: number
  continent?: string
  region?: string
  capital?: string
  timezone?: string
  latitude?: number
  longitude?: number
  zoomLevel?: number
  open: 'y' | 'n'
  menuOpen: 'y' | 'n'
  newIcon: 'y' | 'n'
  languageCode: string
  fileTypedPk?: string
  fileTypedPkFileData?: DYFileFormData
  nearbyNations?: number[]
}

// ============================================ REPOSITORY ============================================

export function useNationInfoApi() {
  // [2026-06-05] 별칭 도메인(web-mfg) 대응 — useAppConfig 직접 사용 시 API_BASE_URL이 myfirstguide로 남아 별칭에서 차단됨
  const { API_BASE_URL } = useDYAppConfig()

  // ── edge(CloudFlare) 캐시 정책 ──────────────────────────────────────────
  // EDGE_TTL = 기본 edge TTL. 호출자가 params.edgeCache 로 명시하면 그 값이 우선, 없으면 EDGE_TTL.
  // (2026-06-24 정책: 호출자 오버라이드 허용 — 백엔드 EdgeCacheRegistry 가 per-URL TTL 로 변형마다
  //  정확 purge → 파편화는 효율 비용일 뿐 정합 문제 없음.) 허용값은 EdgeCacheParams['edgeCache'] union
  // (2h/6h/12h/24h — CF Cache Rule 매칭값). @see docs/NATION-CACHE.md
  // 상세(get/getByIsoCode2)의 latestWeather(동적)도 edge 허용 (정책 결정 2026-06-18).
  const EDGE_TTL: EdgeCacheParams['edgeCache'] = '2h'

  const get = async (nationNo: number, params?: NationInfoGetParams): Promise<NationInfo> => {
    // edgeCache: 호출자 지정 우선, 없으면 EDGE_TTL 기본. 2026-07-07 $fields forward(over-fetch 제거)
    const response = await useDYFetch<NationInfo>(API_BASE_URL+'/api/nation-infos/get-by-no/'+nationNo, {method:'get', query: { ...params, $fields: params?.$fields ? JSON.stringify(params.$fields) : undefined, edgeCache: params?.edgeCache ?? EDGE_TTL }, immediate: false})
    await response.execute({dedupe: 'defer'})

    return extractDataOrFailure(response)
  }

  const getByIsoCode2 = async (isoCode2: string, params?: NationInfoGetParams): Promise<NationInfo> => {
    // 2026-07-07 $fields forward(over-fetch 제거)
    const response = await useDYFetch<NationInfo>(`${API_BASE_URL}/api/nation-infos/${isoCode2}`, {method:'get', query: { ...params, $fields: params?.$fields ? JSON.stringify(params.$fields) : undefined, edgeCache: params?.edgeCache ?? EDGE_TTL }, immediate: false})
    await response.execute({dedupe: 'defer'})

    return extractDataOrFailure(response)
  }

  const getList = async (params?: NationInfoListParams): Promise<ApiListStructure<NationInfo>> => {
    return await useDYFetchData<ApiListStructure<NationInfo>>(`${API_BASE_URL}/api/nation-infos`,
      { query: { ...params, $fields: typeof params?.$fields === 'string' ? params?.$fields : JSON.stringify(params?.$fields), edgeCache: params?.edgeCache ?? EDGE_TTL } }
    )
  }

  const store = async(body:NationInfoStoreParams) => {
    return await $dy__fetchWithToken<{message:string}>(`${API_BASE_URL}/api/nation-infos`,
      { method:'post', body }
    )
  }

  const update = async(pk:number, body:NationInfoStoreParams) => {
    return await $dy__fetchWithToken<{message:string}>(`${API_BASE_URL}/api/nation-infos/${pk}`,
      { method:'patch', body }
    )
  }

  const destroy = async(pk:number) => {
    return await $dy__fetchWithToken<{message:string}>(`${API_BASE_URL}/api/nation-infos/${pk}`,
      { method:'delete' }
    )
  }

  const uploadIntroduceMovie = async (nationPk: number, $data: any) => {
    return await $dy__fetchWithToken<{ Bucket: string, Key: string } | null>(`${API_BASE_URL}/api/nation-infos/upload-introduce-movie/${nationPk}`, { method: 'post', body: $data })
  }

  const changeRank = async(params:DataTableEmitRank) => {
    return await $dy__fetchWithToken<any>(`${API_BASE_URL}/api/nation-infos/change-rank`, { method: 'post', body:params })
}

  return {
    get, getByIsoCode2, getList, uploadIntroduceMovie, store, update, destroy, changeRank
  }
}

// ============================================ REPOSITORY ============================================
